import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
} from '@mui/material';

interface EmailVerificationPopupProps {
  open: boolean;
  email: string;
  /** True while a request (send/verify) is in flight */
  busy?: boolean;
  /** Error message to show below the input */
  error?: string | null;
  /** Optional info message (e.g. "A new code was sent") */
  info?: string | null;
  isRTL?: boolean;
  onCancel: () => void;
  onResend: () => void;
  onValidate: (code: string) => void;
}

export const EmailVerificationPopup: React.FC<EmailVerificationPopupProps> = ({
  open,
  email,
  busy = false,
  error,
  info,
  isRTL = false,
  onCancel,
  onResend,
  onValidate,
}) => {
  const [code, setCode] = useState('');

  const handleValidate = () => {
    if (code.trim()) {
      onValidate(code.trim());
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
      <DialogTitle sx={{ color: '#3A7380', fontWeight: 600, fontFamily: '"Open Sans", sans-serif' }}>
        Verify your email
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          sx={{ color: '#5A7380', mb: 2, fontFamily: '"Open Sans", sans-serif' }}
        >
          You got an email with a code{email ? <> at <strong>{email}</strong></> : ''}. Please enter the code below.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleValidate();
          }}
          placeholder="123456"
          inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: '0.3em' } }}
          error={!!error}
          disabled={busy}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: 'white' },
          }}
        />
        {error && (
          <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
            {error}
          </Typography>
        )}
        {!error && info && (
          <Typography variant="caption" sx={{ color: '#3A7380', mt: 0.5, display: 'block' }}>
            {info}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onCancel} disabled={busy} sx={{ color: '#616161', textTransform: 'none' }}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onResend} disabled={busy} sx={{ color: '#3A7380', textTransform: 'none' }}>
          Send again
        </Button>
        <Button
          onClick={handleValidate}
          variant="contained"
          disabled={busy || !code.trim()}
          sx={{
            backgroundColor: '#3A7380',
            color: 'white',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { backgroundColor: '#2e5c66' },
            '&:disabled': { backgroundColor: '#B0B0B0' },
          }}
        >
          {busy ? 'Please wait…' : 'Validate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
